import axios from 'axios';

const DEFAULT_API_BASE_URL = 'https://repeaked-backend-production.up.railway.app';
export const BETA_ACCESS_STORAGE_KEY = 'repeaked:beta-access-token';
export const BETA_LOCKOUT_STORAGE_KEY = 'repeaked:beta-lockout-until';
export const BETA_FAIL_COUNT_STORAGE_KEY = 'repeaked:beta-fail-count';
export const BETA_MAX_ATTEMPTS = 5;
export const BETA_LOCKOUT_MS = 5 * 60 * 1000;
export const MAILCHIMP_POST_URL = process.env.REACT_APP_MAILCHIMP_POST_URL
  || 'https://repeaked.us14.list-manage.com/subscribe/post?u=eb14ef3d4ef19d999b077f6cf&id=d91c753d71&f_id=009794e1f0';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

export const clearSessionExpiredPopup = () => {
  document.getElementById('repeaked-session-popup')?.remove();
};

const showSessionExpiredPopup = () => {
  const existing = document.getElementById('repeaked-session-popup');
  if (existing) return;

  const overlay = document.createElement('div');
  overlay.id = 'repeaked-session-popup';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:99999;font-family:Rajdhani,sans-serif;';
  overlay.innerHTML = `
    <div style="background:#111;border:1px solid #E8192C40;border-radius:8px;padding:40px 48px;text-align:center;max-width:380px;width:90%;">
      <div style="font-family:'Bebas Neue',sans-serif;font-size:22px;letter-spacing:4px;color:#F0F2F5;margin-bottom:8px;">SESSION EXPIRED</div>
      <div style="font-size:13px;color:#555;letter-spacing:1px;margin-bottom:28px;">You've been logged out. Please sign in again to continue.</div>
      <button onclick="window.location.href='/login'" style="background:#E8192C;color:#fff;border:none;border-radius:4px;padding:12px 32px;font-family:'Bebas Neue',sans-serif;font-size:14px;letter-spacing:3px;cursor:pointer;width:100%;">SIGN IN</button>
    </div>
  `;
  document.body.appendChild(overlay);
};

api.interceptors.request.use((config) => {
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('cached_user');
      localStorage.removeItem('cached_stats');
      if (!error.config?.skipSessionExpiredPopup) {
        showSessionExpiredPopup();
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/login', data, { skipSessionExpiredPopup: true });
export const register = (data) => api.post('/register', data);
export const verifyRegistration = (data) => api.post('/register/verify', data);
export const verifyBetaCode = (betaCode) => api.post('/beta/verify', { beta_code: betaCode });
export const logoutSession = () => api.post('/logout');
export const getMe = (config = {}) => api.get('/me', config);
export const isBetaGateEnabled = () => process.env.REACT_APP_BETA_GATE_ENABLED !== 'false';

const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1];
    if (!base64) return null;
    const normalized = base64.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
};

export const hasValidBetaAccess = () => {
  const token = getStoredBetaAccessToken();
  if (!token) return false;

  const payload = decodeJwtPayload(token);
  if (!payload || payload.purpose !== 'beta_access' || payload.sub !== 'private-beta') {
    clearStoredBetaAccessToken();
    return false;
  }

  if (!payload.exp || Date.now() >= payload.exp * 1000) {
    clearStoredBetaAccessToken();
    return false;
  }

  return true;
};

export const getBetaLockoutRemainingMs = () => {
  try {
    const until = Number.parseInt(sessionStorage.getItem(BETA_LOCKOUT_STORAGE_KEY) || '0', 10);
    if (!until || until <= Date.now()) {
      sessionStorage.removeItem(BETA_LOCKOUT_STORAGE_KEY);
      sessionStorage.removeItem(BETA_FAIL_COUNT_STORAGE_KEY);
      return 0;
    }
    return until - Date.now();
  } catch {
    return 0;
  }
};

export const recordBetaFailedAttempt = () => {
  try {
    const count = Number.parseInt(sessionStorage.getItem(BETA_FAIL_COUNT_STORAGE_KEY) || '0', 10) + 1;
    sessionStorage.setItem(BETA_FAIL_COUNT_STORAGE_KEY, String(count));
    if (count >= BETA_MAX_ATTEMPTS) {
      sessionStorage.setItem(BETA_LOCKOUT_STORAGE_KEY, String(Date.now() + BETA_LOCKOUT_MS));
    }
    return count;
  } catch {
    return BETA_MAX_ATTEMPTS;
  }
};

export const clearBetaFailedAttempts = () => {
  try {
    sessionStorage.removeItem(BETA_FAIL_COUNT_STORAGE_KEY);
    sessionStorage.removeItem(BETA_LOCKOUT_STORAGE_KEY);
  } catch {
    // Ignore storage errors.
  }
};

export const getStoredBetaAccessToken = () => {
  try {
    return localStorage.getItem(BETA_ACCESS_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};
export const storeBetaAccessToken = (betaToken) => {
  if (!betaToken) return;
  try {
    localStorage.setItem(BETA_ACCESS_STORAGE_KEY, betaToken);
  } catch {
    // Beta approval is convenience-only; backend still validates every signup.
  }
};
export const clearStoredBetaAccessToken = () => {
  try {
    localStorage.removeItem(BETA_ACCESS_STORAGE_KEY);
  } catch {
    // Ignore storage errors.
  }
};

export const subscribeMailchimp = (email) => new Promise((resolve, reject) => {
  const trimmedEmail = email.trim();
  if (!MAILCHIMP_POST_URL || !trimmedEmail) {
    reject(new Error('Email signup is not configured'));
    return;
  }

  const callbackName = `mailchimpCallback_${Date.now()}`;
  const jsonpUrl = `${MAILCHIMP_POST_URL.replace('/post?', '/post-json?')}&EMAIL=${encodeURIComponent(trimmedEmail)}&c=${callbackName}`;

  const script = document.createElement('script');
  const cleanup = () => {
    delete window[callbackName];
    script.remove();
  };

  const timeout = setTimeout(() => {
    cleanup();
    reject(new Error('Signup timed out — try again'));
  }, 10000);

  window[callbackName] = (data) => {
    clearTimeout(timeout);
    cleanup();
    if (data?.result === 'success') {
      resolve(data);
      return;
    }
    if (data?.msg?.includes('already subscribed')) {
      resolve(data);
      return;
    }
    reject(new Error(data?.msg || 'Something went wrong — try again'));
  };

  script.src = jsonpUrl;
  script.onerror = () => {
    clearTimeout(timeout);
    cleanup();
    reject(new Error('Something went wrong — try again'));
  };

  document.body.appendChild(script);
});

// Queue
export const joinQueue = (mode) => api.post(`/queue/join?mode=${mode}`);
export const joinCasualQueue = (map_name) => api.post('/queue/join/casual', { map_name });
export const leaveQueue = () => api.post('/queue/leave');
export const getQueueStatus = () => api.get('/queue/status');

// Match
export const acceptMatch = (matchId) => api.post(`/match/accept/${matchId}`);
export const declineMatch = (matchId) => api.post(`/match/decline/${matchId}`);
export const getMatch = (matchId) => api.get(`/match/${matchId}`);
export const getCompletedMatches = ({ page = 1, limit = 10, mode } = {}) =>
  api.get('/matches/completed', { params: { page, limit, ...(mode ? { mode } : {}) } });
export const getVetoStatus = (matchId) => api.get(`/match/${matchId}/veto-status`);
export const vetoMap = (matchId, map_name) => api.post(`/match/${matchId}/veto?map_name=${map_name}`);
export const chooseSide = (matchId, side) => api.post(`/match/${matchId}/choose-side?side=${side}`);

// Profile
export const getProfile = (username) => api.get(`/profile/${username}`);
export const getPlayerRank = (username, mode) => api.get(`/profile/${username}/rank`, { params: mode ? { mode } : undefined });
export const updateProfile = (data) => api.post('/profile/update', data);
export const changeUsername = (username) => api.post('/profile/change-username', { username });
export const getProfileComments = (username) => api.get(`/profile/${encodeURIComponent(username)}/comments`);
export const createProfileComment = (username, content) => api.post(`/profile/${encodeURIComponent(username)}/comments`, { content });
export const deleteProfileComment = (username, commentId) => api.delete(`/profile/${encodeURIComponent(username)}/comments/${encodeURIComponent(commentId)}`);
export const uploadAvatar = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/profile/avatar/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Leaderboard
export const getLeaderboard = ({ page = 1, limit = 10, mode = 'fivevfive' } = {}) => api.get('/leaderboard', { params: { page, limit, mode } });
export const getMyLeaderboardRank = (limit = 10, mode = 'fivevfive') => api.get('/leaderboard/me', { params: { limit, mode } });

// Maps
export const getMaps = (mode) => api.get(`/maps?mode=${mode}`);

// Shop
export const getShop = () => api.get('/shop');
export const buySkin = (skinId) => api.post(`/shop/buy/${skinId}`);
export const getInventory = (username) => username ? api.get(`/inventory/${encodeURIComponent(username)}`) : api.get('/shop/inventory');

// Payments
export const createCheckout = (package_name) => api.post(`/payments/create-checkout?package=${package_name}`);

// Notifications
export const getNotifications = () => api.get('/notifications');
export const readNotification = (id) => api.post(`/notifications/read/${id}`);
export const sendPartyInvite = (username) => api.post('/party/invite', { username });
export const respondPartyInvite = (notifId, accepted) => api.post(`/party/invite/respond?notif_id=${notifId}&accepted=${accepted}`);
export const getCurrentParty = () => api.get('/party/current');
export const leaveParty = () => api.post('/party/leave');
export const joinParty = (partyId) => api.post(`/party/join/${partyId}`);
export const kickFromParty = (userId) => api.post(`/party/kick/${userId}`);
export const promotePartyLeader = (userId) => api.post(`/party/promote/${userId}`);
export const setPartyMode = (mode) => api.post('/party/mode', { mode });


// Friends
export const getFriends = () => api.get('/friends');
export const getFriendsByUsername = (username) => api.get(`/friends/${username}`);
export const searchUsers = (q) => api.get(`/users/search?q=${encodeURIComponent(q)}`);
export const getMatchHistory = (username) => api.get(`/profile/${username}/match-history`);
export const getMatchStats = (lobbyId) => api.get(`/custom-lobby/${lobbyId}/stats`);
export const addFriend = (username) => api.post('/friends/add', { friend_username: username });
export const getFriendRequests = () => api.get('/friends/requests');
export const acceptFriend = (requesterId) => api.post(`/friends/accept/${requesterId}`);

export const steamLogin = (betaToken) => {
  const query = betaToken ? `?beta_token=${encodeURIComponent(betaToken)}` : '';
  window.location.href = `${API_BASE_URL}/auth/steam${query}`;
};

export const getEloHistory = () => api.get('/stats/elo-history');

// Inventory
export const equipSkin = (skinId, weaponId) => api.post('/inventory/equip', { skin_id: skinId, weapon_id: weaponId });

// Kits
export const buyKit = (type) => api.post('/shop/buy-kit', { type });
export const applyStatTrak = (inventoryId) => api.post('/inventory/apply-stattrak', { inventory_id: inventoryId });
export const applyNametag = (inventoryId, nametag) => api.post('/inventory/apply-nametag', { inventory_id: inventoryId, nametag });
export const removeStatTrak = (inventoryId) => api.post('/inventory/remove-stattrak', { inventory_id: inventoryId });
export const setWeaponPreference = (slot, weapon) => api.post('/inventory/weapon-preference', { slot, weapon });

// Custom Lobbies (Fixed API_BASE and manual Auth headers)
export const createCustomLobby = async () => {
  const res = await api.post('/custom-lobby/create');
  return res.data;
};
 
export const getCustomLobby = async (lobbyId) => {
  const res = await api.get(`/custom-lobby/${lobbyId}`);
  return res.data;
};

export const getCurrentCustomLobby = async () => {
  const res = await api.get('/custom-lobby/current');
  return res.data;
};
 
export const joinCustomLobby = async (code) => {
  const res = await api.post(`/custom-lobby/join?code=${code}`);
  return res.data;
};
 
export const leaveCustomLobby = async (lobbyId) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/leave`);
  return res.data;
};
 
export const setLobbyMode = async (lobbyId, mode) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/mode`, {}, { params: { mode } });
  return res.data;
};
 
export const setLobbyMap = async (lobbyId, mapId) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/map?map_id=${mapId}`);
  return res.data;
};

export const setLobbyRegion = async (lobbyId, region) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/region`, {}, { params: { region } });
  return res.data;
};
 
export const assignTeam = async (lobbyId, userId, team) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/assign`, {}, { params: { user_id: userId, team } });
  return res.data;
};
 
export const randomiseTeams = async (lobbyId) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/randomise`);
  return res.data;
};
 
export const promoteToCaptain = async (lobbyId, userId) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/promote?user_id=${userId}`);
  return res.data;
};
 
export const kickFromLobby = async (lobbyId, userId) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/kick?user_id=${userId}`);
  return res.data;
};
 
export const setKnifeRound = async (lobbyId, enabled) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/knife-round?enabled=${enabled}`);
  return res.data;
};
 
export const startCustomMatch = async (lobbyId) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/start`);
  return res.data;
};

export const setTeamLock = async (lobbyId, locked) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/team-lock?locked=${locked}`);
  return res.data;
};
 
export const addBots = async (lobbyId, team, count) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/bots/add`, {}, { params: { team, count } });
  return res.data;
};
 
export const removeBot = async (lobbyId, botId) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/bots/remove?bot_id=${botId}`);
  return res.data;
};

export const clearBots = async (lobbyId, team) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/bots/clear?team=${team}`);
  return res.data;
};

export const resetLobby   = async (lobbyId) => { const r = await api.post(`/custom-lobby/${lobbyId}/reset`);   return r.data; };
export const disbandLobby = async (lobbyId) => { const r = await api.post(`/custom-lobby/${lobbyId}/disband`); return r.data; };

export const setTeamName = (lobbyId, team, name) =>
  api.post(`/custom-lobby/${lobbyId}/team-name?team=${team}&name=${encodeURIComponent(name)}`);

export const setAllTalk = async (lobbyId, enabled) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/all-talk?enabled=${enabled}`);
  return res.data;
};

export const setTeamNamesLock = async (lobbyId, locked) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/team-names-lock?locked=${locked}`);
  return res.data;
};

export const getPublicLobbies = () => api.get('/custom-lobby/public');
export const setLobbyVisibility = async (lobbyId, isPublic) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/visibility?is_public=${isPublic}`);
  return res.data;
};
export const inviteFriendToLobby = async (lobbyId, username) => {
  const res = await api.post(`/custom-lobby/${lobbyId}/invite-friend?friend_username=${encodeURIComponent(username)}`);
  return res.data;
};

// Bug reports
export const submitBugReport = ({ description, category, pageUrl, images }) => {
  const formData = new FormData();
  formData.append('description', description);
  formData.append('category', category);
  if (pageUrl) formData.append('page_url', pageUrl);
  images.forEach((file) => formData.append('images', file));
  return api.post('/bug-reports', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Admin
export const getAdminMe = () => api.get('/admin/me');
export const listBugReports = (params = {}) => api.get('/admin/bug-reports', { params });
export const getBugReport = (reportId) => api.get(`/admin/bug-reports/${reportId}`);
export const updateBugReport = (reportId, data) => api.patch(`/admin/bug-reports/${reportId}`, data);
export const deleteBugReport = (reportId) => api.delete(`/admin/bug-reports/${reportId}`);

export default api;